/**
 * Gaussian blur of one alpha plane, as three box blurs (Kovesi, « Fast almost-
 * Gaussian filtering »). The PDF's shadow raster uses this; the editor uses CSS
 * `blur(σ)`, a true Gaussian — three boxes are within a few % of it, invisible
 * on a soft shadow. Deterministic and canvas-free, so it is tested in node and
 * does not depend on `ctx.filter` support in the render browser.
 */

/** Widths of `n` boxes whose stacked variance ≈ sigma². Odd widths. */
export function boxesForGauss(sigma: number, n = 3): number[] {
  const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) wl--;
  const wu = wl + 2;
  const mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
  const m = Math.round(mIdeal);
  return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
}

/** One running-sum box pass along a line of `len` samples (outside = 0). */
function boxLine(src: Float32Array, dst: Float32Array, start: number, step: number, len: number, r: number) {
  const inv = 1 / (2 * r + 1);
  let acc = 0;
  for (let k = 0; k <= r && k < len; k++) acc += src[start + k * step];
  for (let k = 0; k < len; k++) {
    dst[start + k * step] = acc * inv;
    const add = k + r + 1, sub = k - r;
    if (add < len) acc += src[start + add * step];
    if (sub >= 0) acc -= src[start + sub * step];
  }
}

/** Blur `alpha` (w×h, 0..255) with standard deviation `sigmaPx`. Outside the
 *  plane counts as transparent, so pad the canvas by ≥ 3σ before calling. */
export function blurAlpha(alpha: Uint8ClampedArray, w: number, h: number, sigmaPx: number): Uint8ClampedArray {
  if (!(sigmaPx > 0)) return new Uint8ClampedArray(alpha);
  const a = Float32Array.from(alpha);
  const b = new Float32Array(a.length);
  for (const size of boxesForGauss(sigmaPx, 3)) {
    const r = (size - 1) / 2;
    for (let y = 0; y < h; y++) boxLine(a, b, y * w, 1, w, r);   // rows: a → b
    for (let x = 0; x < w; x++) boxLine(b, a, x, w, h, r);       // columns: b → a
  }
  const out = new Uint8ClampedArray(alpha.length);
  for (let i = 0; i < out.length; i++) out[i] = Math.round(a[i]);
  return out;
}
